import {
  type BarData,
  FormatType,
  GroupedBarChart,
  type GroupedBarData,
  GroupedBarTooltip,
  getFormatter,
  ReplotChartNames,
} from '@revelio/replots';

import { usePlotMetric } from '../../hooks/usePlotMetric';
import type { BreakdownSingleDataSet } from '../DataProvider/types';
import { useChart } from './ChartProvider';
import { usePlotShareData } from './chart-menu/plot-share/usePlotShare';
import { getFormat } from './utils/getFormat';
import { getActiveColor, useActiveColors } from './utils/useActiveColors';

type ShareDataPoint = { share?: number };
type GroupedBarWithShareData = GroupedBarData<ShareDataPoint>;

export const GroupedBar = ({
  data,
  hideLegend,
  ignoreCustomTooltip,
}: {
  data: BreakdownSingleDataSet['data'];
  hideLegend?: boolean;
  ignoreCustomTooltip?: boolean;
}) => {
  const { item } = useChart();
  const metric = usePlotMetric();

  const { colorMap } = useActiveColors();

  const format = item?.plot?.format ?? getFormat({ metric });
  const showBarLabels = item?.plot?.showDataLabels ?? false;
  const orientation = item?.plot?.orientation ?? 'horizontal';

  const groupedBarData: GroupedBarWithShareData[] = data?.map((item) => ({
    group: item.dim,
    values: item.breakdown.map((breakdown) => ({
      label: breakdown.dim,
      value: breakdown.value ?? 0,
      share: breakdown.share ?? 0,
      id: breakdown.id ?? null,
      color: getActiveColor({
        colorMap,
        dimension: breakdown.dim,
        id: breakdown.id,
      }),
    })),
  }));

  const shortNameColorMap = (() => {
    const base: Record<string, string> = { ...(colorMap ?? {}) };
    const breakdownRows = data?.flatMap((d) => d.breakdown) ?? [];

    for (const breakdown of breakdownRows) {
      if (breakdown.id == null) continue;
      const idStr = String(breakdown.id);
      const shortKey = `${idStr}_${breakdown.dim}`;
      if (breakdown.longName) {
        const longKey = `${idStr}_${breakdown.longName}`;
        if (!base[shortKey] && base[longKey]) {
          base[shortKey] = base[longKey];
        }
      }
    }

    for (const breakdown of breakdownRows) {
      if (breakdown.id == null) continue;
      const idStr = String(breakdown.id);
      const shortKey = `${idStr}_${breakdown.dim}`;
      if (base[shortKey]) continue;
      const prefix = `${idStr}_`;
      const match = Object.entries(base).find(([k]) => k.startsWith(prefix));
      if (match) {
        base[shortKey] = match[1];
      }
    }

    return base;
  })();

  const tooltipContent = (group: string, bar: BarData & ShareDataPoint) => {
    const tooltipValue = (() => {
      if (format === FormatType.PERCENTAGE) {
        return getFormatter(format)(bar?.value ?? 0);
      }

      const formattedValue = getFormatter(FormatType.INTEGER)(bar?.value || 0);
      const formattedShare = getFormatter(FormatType.PERCENTAGE)(
        bar?.share ?? 0
      );
      return bar?.share !== undefined
        ? `${formattedValue} / ${formattedShare}`
        : formattedValue;
    })();

    return (
      <GroupedBarTooltip title={bar.label} label={group} value={tooltipValue} />
    );
  };

  const chartProps = {
    data: groupedBarData,
    format,
    colorMap: shortNameColorMap,
    colorPalette: item?.plot?.colorPalette,
    ...(!ignoreCustomTooltip && { tooltipContent }),
    hideLegend,
    showBarLabels,
    orientation,
  };

  usePlotShareData({
    data: groupedBarData,
    chartType: ReplotChartNames.GroupedBarChartHorizontal,
    chartProps,
  });

  return <GroupedBarChart<ShareDataPoint> {...chartProps} />;
};
